import type {
  Classroom,
  WritingCompanionPolicy,
  WritingCompanionPurchasePolicy,
} from "~/classes/models/classrooms/classroom.model";
import { normalizeWritingCompanionPolicy } from "~/classes/models/classrooms/classroom.model";

export const WRITING_COMPANION_ACTIVATION_RETRY_MS = 90_000;
export const WRITING_COMPANION_ACTIVATION_RETRY_INTERVAL_MS = 5_000;

export const retryWritingCompanionActivation = async <T>({
  activate,
  timeoutMs = WRITING_COMPANION_ACTIVATION_RETRY_MS,
  retryIntervalMs = WRITING_COMPANION_ACTIVATION_RETRY_INTERVAL_MS,
  now = Date.now,
  wait = (delayMs: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, delayMs)),
}: {
  activate: () => Promise<T>;
  timeoutMs?: number;
  retryIntervalMs?: number;
  now?: () => number;
  wait?: (delayMs: number) => Promise<void>;
}) => {
  const startedAt = now();

  while (true) {
    try {
      return await activate();
    } catch (error) {
      const remainingMs = timeoutMs - (now() - startedAt);
      if (remainingMs <= 0) throw error;
      await wait(Math.min(retryIntervalMs, remainingMs));
    }
  }
};

type WritingCompanionAccessOptions = {
  policy?: WritingCompanionPolicy | WritingCompanionPurchasePolicy;
  hasPurchased: boolean;
  isLoading?: boolean;
  isActivationPending?: boolean;
  isVerificationUnavailable?: boolean;
  legacyEnabled?: boolean;
  legacyStudentIds?: string[];
  legacyCanRunChecklist?: boolean;
  legacyCanUseThesaurus?: boolean;
  studentId?: string;
};

export const isWritingCompanionPolicyEnabled = (
  policy?: WritingCompanionPolicy | WritingCompanionPurchasePolicy
) => normalizeWritingCompanionPolicy(policy) !== "off";

export const getWritingCompanionClassroomAccess = ({
  policy,
  hasPurchased,
  isLoading = false,
  isActivationPending = false,
  isVerificationUnavailable = false,
  legacyEnabled = false,
  legacyStudentIds = [],
  legacyCanRunChecklist = false,
  legacyCanUseThesaurus = false,
  studentId,
}: WritingCompanionAccessOptions) => {
  const hasLegacyWritingAccess =
    legacyEnabled &&
    studentId != undefined &&
    legacyStudentIds.includes(studentId);

  if (policy == undefined) {
    return {
      usesLegacyPolicy: true,
      showPurchasePrompt: false,
      canDismissPurchasePrompt: false,
      canUpload: true,
      canWrite: hasLegacyWritingAccess,
      canRunChecklist: legacyCanRunChecklist,
      canUseThesaurus: legacyCanUseThesaurus,
    };
  }

  const normalizedPolicy = normalizeWritingCompanionPolicy(policy);
  const isPurchaseEnabled =
    normalizedPolicy === "student_optional" ||
    normalizedPolicy === "student_required";
  const isSponsored =
    normalizedPolicy === "teacher_sponsored" ||
    normalizedPolicy === "organization_sponsored";
  const isEntitled = (isPurchaseEnabled || isSponsored) && hasPurchased;
  const isVerificationBlocked =
    isLoading || isActivationPending || isVerificationUnavailable;

  return {
    usesLegacyPolicy: false,
    showPurchasePrompt:
      isPurchaseEnabled && !hasPurchased && !isVerificationBlocked,
    canDismissPurchasePrompt: normalizedPolicy === "student_optional",
    canUpload:
      normalizedPolicy !== "student_required" && !isSponsored
        ? true
        : isEntitled,
    canWrite: isEntitled || hasLegacyWritingAccess,
    canRunChecklist: isEntitled || legacyCanRunChecklist,
    canUseThesaurus: isEntitled || legacyCanUseThesaurus,
  };
};

export const getWritingCompanionPurchasePromptClassrooms = <
  T extends Pick<
    Classroom,
    "id" | "writingCompanionPurchasePolicy" | "writingCompanionPolicy"
  >,
>({
  classrooms,
  dismissedClassroomIds,
  hasPurchased,
  isLoading,
  hasPurchasedClassroomIds,
  loadingClassroomIds,
}: {
  classrooms: T[];
  dismissedClassroomIds: string[];
  hasPurchased: boolean;
  isLoading: boolean;
  hasPurchasedClassroomIds?: string[];
  loadingClassroomIds?: string[];
}) => {
  return classrooms.filter((classroom) => {
    const classroomId = classroom.id ?? "";
    const classroomHasPurchased = hasPurchasedClassroomIds
      ? hasPurchasedClassroomIds.includes(classroomId)
      : hasPurchased;
    const classroomIsLoading = loadingClassroomIds
      ? loadingClassroomIds.includes(classroomId)
      : isLoading;
    if (classroomHasPurchased || classroomIsLoading) return false;
    const policy = normalizeWritingCompanionPolicy(
      classroom.writingCompanionPolicy ??
        classroom.writingCompanionPurchasePolicy
    );

    if (policy !== "student_optional" && policy !== "student_required")
      return false;

    return (
      policy === "student_required" ||
      !dismissedClassroomIds.includes(classroom.id ?? "")
    );
  });
};

export const addWritingCompanionPurchaseDismissal = (
  dismissals: Record<string, string[]>,
  studentId: string,
  classroomId: string
) => {
  return {
    ...dismissals,
    [studentId]: Array.from(
      new Set([...(dismissals[studentId] ?? []), classroomId])
    ),
  };
};

export const isValidCardExpiration = (
  month: string,
  year: string,
  now = new Date()
) => {
  if (!/^(0[1-9]|1[0-2])$/.test(month) || !/^\d{4}$/.test(year)) {
    return false;
  }

  const expirationMonth = Number(month);
  const expirationYear = Number(year);
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  return (
    expirationYear > currentYear ||
    (expirationYear === currentYear && expirationMonth >= currentMonth)
  );
};

export const getPaymentTokenErrorMessage = (
  error: unknown
): string | undefined => {
  const reason =
    error && typeof error === "object" && "reason" in error
      ? String(error.reason)
      : undefined;

  if (reason === "CREATE_TOKEN_VALIDATION_PARAMS") {
    return "Enter a valid card expiration date.";
  }
  if (
    reason === "CREATE_TOKEN_VALIDATION_FIELDS" ||
    reason === "CREATE_TOKEN_VALIDATION_SERVERSIDE"
  ) {
    return "Check the card number, expiration date, and security code.";
  }
  if (
    reason === "CREATE_TOKEN_TIMEOUT" ||
    reason === "CREATE_TOKEN_XHR_ERROR"
  ) {
    return "The payment service did not respond. Please try again.";
  }
};

export type WritingCompanionValidationField =
  | "firstName"
  | "lastName"
  | "phoneNumber"
  | "streetAddress"
  | "streetAddressLine2"
  | "city"
  | "country"
  | "jurisdiction"
  | "postalCode"
  | "shippingMethod";

export type WritingCompanionValidationErrors = {
  fieldErrors: Partial<Record<WritingCompanionValidationField, string>>;
  messages: string[];
};

const checkoutValidationField = (
  path: string
): WritingCompanionValidationField | undefined => {
  const normalized = path.toLowerCase().replace(/[^a-z]/g, "");

  if (normalized.includes("shippingmethod")) return "shippingMethod";
  if (normalized.includes("firstname")) return "firstName";
  if (normalized.includes("lastname")) return "lastName";
  if (normalized.includes("phone")) return "phoneNumber";
  if (
    normalized.includes("streetaddressline2") ||
    normalized.includes("address2") ||
    normalized.includes("careof") ||
    normalized.includes("suite")
  ) {
    return "streetAddressLine2";
  }
  if (
    normalized.includes("street") ||
    normalized.includes("address1") ||
    normalized.endsWith("address")
  ) {
    return "streetAddress";
  }
  if (normalized.includes("city")) return "city";
  if (normalized.includes("country")) return "country";
  if (
    normalized.includes("state") ||
    normalized.includes("zone") ||
    normalized.includes("county") ||
    normalized.includes("province") ||
    normalized.includes("jurisdiction")
  ) {
    return "jurisdiction";
  }
  if (normalized.includes("postal") || normalized.includes("zip")) {
    return "postalCode";
  }
};

const validationMessage = (value: unknown) => {
  if (typeof value !== "string") return undefined;
  const message = value.trim();
  return message ? message.slice(0, 240) : undefined;
};

export const getWritingCompanionValidationErrors = (
  error: unknown
): WritingCompanionValidationErrors => {
  const response = error as any;
  const details = response?.data?.data || response?.data || {};
  const rawFields = details?.fields;
  const entries: Array<{ path: string; message: string }> = [];

  const collect = (path: string, value: unknown) => {
    const message = validationMessage(value);
    if (message) {
      entries.push({ path, message });
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((child) => collect(path, child));
      return;
    }
    if (!value || typeof value !== "object") return;

    const record = value as Record<string, unknown>;
    const explicitPath = validationMessage(
      record.field || record.path || record.attribute
    );
    const explicitMessage = validationMessage(
      record.message || record.error
    );
    if (explicitMessage) {
      entries.push({ path: explicitPath || path, message: explicitMessage });
      return;
    }

    Object.entries(record).forEach(([key, child]) =>
      collect(path ? `${path}.${key}` : key, child)
    );
  };

  collect("", rawFields);

  const fieldErrors: WritingCompanionValidationErrors["fieldErrors"] = {};
  const messages: string[] = [];
  for (const entry of entries) {
    if (!messages.includes(entry.message) && messages.length < 10) {
      messages.push(entry.message);
    }
    // IEW's OrderAddress validator reports region/country failures under the
    // generic "address" key. Use its message only for those aggregate errors.
    const aggregateAddress = /^(?:(?:billing|shipping))?address$/i.test(entry.path);
    const field = aggregateAddress && /\b(state|zone|province|county)\b/i.test(entry.message)
      ? "jurisdiction"
      : aggregateAddress && /\bcountry\b/i.test(entry.message)
        ? "country"
        : checkoutValidationField(entry.path);
    if (field && !fieldErrors[field]) fieldErrors[field] = entry.message;
  }

  return { fieldErrors, messages };
};
